import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { kebabToPascalCase } from '@panaya/utils';

@Component({
    selector: 'p-expand-area-trigger',
    template: `<div class="read-more-div">
        <span *ngIf="expandable" class="left-actions">
            <span class="read-more-span" *ngIf="addReadMoreSpan"></span>
            <p-link *ngIf="!expanded" (onClick)="triggerReadMore()" class="actions">{{ readMoreText }}</p-link>
            <p-link *ngIf="expanded" (onClick)="triggerReadLess()" class="actions">{{ readLessText }}</p-link>
        </span>
        <span class="right-actions">
            <ng-content select="[right-actions]"></ng-content>
        </span>
    </div>`,
    styleUrls: ['./p-expand-area-trigger.component.scss']
})
export class PExpandAreaTriggerComponent implements AfterViewInit {
    @Input() public element!: HTMLElement;
    @Input() public addReadMoreSpan = false;

    /**
     * Tells the using component to expand the element.
     *
     * When the user clicks the "Read More" button, emits `true`.
     * When the user clicks the "Read Less" button, emits `false`.
     */
    @Output() public readonly expandElement = new EventEmitter<boolean>();

    private readonly cd = inject(ChangeDetectorRef);
    private readonly translateService = inject(TranslateService);

    private _expanded = false;
    public get expanded(): boolean {
        return this._expanded;
    }

    private _expandable = false;
    public get expandable(): boolean {
        return this._expandable;
    }

    protected readonly readMoreText = kebabToPascalCase(this.translateService.instant('Actions.readMore'));
    protected readonly readLessText = kebabToPascalCase(this.translateService.instant('Actions.readLess'));

    private maxCollapsedHeight?: number;

    public ngAfterViewInit(): void {
        this.triggerCheckIsAreaExpandable();
    }

    public triggerCheckIsAreaExpandable(): void {
        // need the setTimeout to wait the dom will change the element offset & scroll height
        setTimeout(() => {
            const hasScroll = this.element.scrollHeight > this.element.offsetHeight;

            if (hasScroll) {
                //kept the max height from dom when reached it.
                this.maxCollapsedHeight = this.element.offsetHeight;
            }

            // Check if it is possible to collapse/expand the seen area (the element height reached the max collapsed height)
            this._expandable = !!this.maxCollapsedHeight && this.element.scrollHeight > this.maxCollapsedHeight;

            if (!this.expandable) {
                this.setExpanded(false);
            }

            // Tell Angular we changed `expandable` and make it reevaluate the `ngIf`
            this.cd.markForCheck();
        });
    }

    public triggerReadLess(): void {
        this.setExpanded(false);
    }

    public triggerReadMore(): void {
        this.setExpanded(true);
    }

    private setExpanded(expanded: boolean): void {
        this._expanded = expanded;
        this.expandElement.emit(expanded);
    }
}
